import type {
  AdminUsageDashboardResponse,
  AdminUsageDepartmentBreakdown,
  AdminUsageModelBreakdown,
  AdminUsageTrendPoint,
} from '@nadoutong/contracts';
import { Meter, StackedBars, stackedBarsSwatchClass } from '../../charts/index.ts';
import type { StackedBarsSeries } from '../../charts/index.ts';
import { formatTokens } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip } from '../../ui/Chip.tsx';
import {
  formatCompactCount,
  formatLatencyMs,
  formatNullablePercent,
  testIdSlug,
  trendBucketAxis,
} from './format.ts';
import type { TrendBucketGranularity } from './format.ts';
import { mixShares } from './modelMix.ts';
import { PanelHeader } from './shared.tsx';

/**
 * The read-only analytic panels of the admin dashboard: the stacked
 * token trend, the model and org-unit mixes, and the service-health
 * readout.
 *
 * They are grouped in one module because they share one series color
 * ramp — the FIXED chart ramp of `src/charts/` (DESIGN.md §2: forest →
 * moss → rust → warn → line-strong). None of them owns a palette; the
 * swatch strip in `ModelMixPanel` echoes `stackedBarsSwatchClass` so a
 * legend chip and its bar are the same slot by construction.
 *
 * That ramp has 5 slots, and every top-N truncation here is derived
 * from it rather than chosen: `SERIES_LIMIT` is the ramp size, because
 * a chart drawing more series than the ramp holds would give two of
 * them the same color (the cycle is modular). Changing the ramp changes
 * the limit, in that order.
 *
 * A cap on COLORS is not a licence to drop data: `ModelMixPanel` sums
 * whatever the ramp cannot name into one `Other` row (#48). See its
 * own note for why.
 *
 * Every panel is a pure function of the slice of the dashboard
 * response it is handed — no queries, no state (the metric toggle is
 * controlled by the page).
 */

/**
 * Series cap for every panel that colors by slot. Equals the
 * `src/charts/` ramp size — see the module note above.
 */
const SERIES_LIMIT = 5;

/**
 * Fill for the aggregated `Other` remainder. Deliberately NOT a sixth
 * ramp slot: DESIGN.md §2 fixes the chart ramp at five hues, and the
 * remainder is not a series — it is everything the ramp had no slot
 * for. Drawing it in the muted neutral keeps "aggregate" and "model"
 * visually different in kind rather than merely different in hue, and
 * it never carries the meaning alone (the row is labelled in text).
 *
 * Whole class literal, never a `prefix-${token}` template — Tailwind's
 * content scanner only sees complete strings, and a well-formed but
 * unknown utility paints nothing without erroring.
 */
const OTHER_SWATCH_CLASS = 'bg-ink-faint';

/**
 * Which token direction the trend stacks.
 * `total` is the cache-inclusive total (uncached + read + write + output),
 * not raw input + output.
 */
export type TokenMetric = 'total' | 'in' | 'out' | 'write' | 'read';

/**
 * Metric ids are the chart's own vocabulary and the `data-testid`
 * suffixes; only the button face is translated.
 */
const METRIC_KEYS: Readonly<Record<TokenMetric, I18nKey>> = {
  total: 'usage.metric.total',
  in: 'usage.metric.in',
  out: 'usage.metric.out',
  write: 'usage.metric.write',
  read: 'usage.metric.read',
};

/** Button order. Write/read reuse the nouns `MemberUsage` already shows. */
const TREND_METRICS = [
  'total',
  'in',
  'out',
  'write',
  'read',
] as const satisfies readonly TokenMetric[];

/** Controlled total/in/out/write/read switch, rendered as the trend panel's header action. */
export function MetricToggle(props: {
  readonly metric: TokenMetric;
  readonly onChange: (metric: TokenMetric) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="inline-flex items-center gap-1 rounded-lg bg-raised p-1">
      {TREND_METRICS.map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => props.onChange(item)}
          className={[
            'whitespace-nowrap rounded-md px-3 py-1.5 text-caption font-medium uppercase transition-colors',
            props.metric === item ? 'bg-card text-forest' : 'text-ink-low hover:text-ink',
          ].join(' ')}
          data-testid={`usage-trend-metric-${item}`}
        >
          {t(METRIC_KEYS[item])}
        </button>
      ))}
    </div>
  );
}

/**
 * Token trend as one stacked bar per time bucket. The trend response is
 * a flat point list keyed by (bucket, group), so the series set is
 * derived here and handed to `StackedBars`, which owns the drawing and
 * the legend.
 *
 * The bucket AXIS is not derived from the response — it is materialized
 * from the window by `trendBucketAxis`, because `GROUP BY` emits no row
 * for a bucket with no traffic and reading the returned keys as the
 * axis silently shortened the chart (#46). Buckets the server returned
 * from outside that axis are kept, never dropped.
 *
 * An entirely empty window still shows the empty copy rather than a
 * zero-filled chart: thirty flat bars say less than one sentence.
 */
export function StackedTrendChart(props: {
  readonly points: readonly AdminUsageTrendPoint[];
  readonly metric: TokenMetric;
  readonly window: {
    readonly from: string;
    readonly to: string;
    readonly granularity: TrendBucketGranularity;
  };
}): JSX.Element {
  const t = useT();
  if (props.points.length === 0) {
    return (
      <div
        data-testid="usage-admin-trend-empty"
        className="flex h-72 items-center justify-center rounded-lg bg-raised text-body text-ink-low"
      >
        {t('usage.admin.empty.window')}
      </div>
    );
  }
  const axis = trendBucketAxis({
    window: { from: props.window.from, to: props.window.to },
    granularity: props.window.granularity,
    serverBuckets: props.points.map((point) => point.bucket),
  });
  const series = pivotTrend(props.points, props.metric, axis.buckets);
  return (
    <div className="flex flex-col gap-3">
      <StackedBars
        buckets={axis.buckets}
        series={series}
        incomplete={axis.incomplete}
        incompleteLabel={t('usage.admin.trend.incomplete')}
        ariaLabel={t('usage.admin.trend.title')}
        testId="usage-admin-trend-chart"
      />
      {axis.incomplete.some((flag) => flag) ? (
        <p
          className="m-0 text-caption text-ink-low"
          data-testid="usage-admin-trend-incomplete-note"
        >
          {t('usage.admin.trend.incomplete_note')}
        </p>
      ) : null}
    </div>
  );
}

/** The models past the ramp, summed into the one row that stands for them. */
interface ModelMixRemainder {
  readonly count: number;
  readonly uncachedInputTokens: number;
  readonly outputTokens: number;
  readonly cacheWriteTokens: number;
  readonly cacheReadTokens: number;
  readonly totalTokens: number;
  readonly share: number;
}

/**
 * A count the contract requires, read as optional.
 *
 * The console can ship before the server that sends `uncachedInputTokens`
 * / `cacheWriteTokens` / `cacheReadTokens` — the same gap `vendorLine`
 * exists for. A missing key is `undefined` at runtime even though the
 * type says `number`. Only absence takes `fallback`; a present 0 stays 0.
 */
function countOr(value: number | undefined, fallback: number): number {
  return value ?? fallback;
}

/**
 * Sum the models the ramp had no slot for, or `null` when there are
 * none — an empty `Other` row would be a worse lie than no row.
 *
 * The share is summed from the tail's own shares rather than taken as
 * `1 - top`, so a server whose shares do not quite reach 1 shows that
 * shortfall instead of having it silently absorbed by the aggregate.
 */
function summarizeRemainder(rest: readonly AdminUsageModelBreakdown[]): ModelMixRemainder | null {
  if (rest.length === 0) return null;
  return rest.reduce<ModelMixRemainder>(
    (acc, model) => ({
      count: acc.count + 1,
      uncachedInputTokens:
        acc.uncachedInputTokens + countOr(model.uncachedInputTokens, model.inputTokens),
      outputTokens: acc.outputTokens + model.outputTokens,
      cacheWriteTokens: acc.cacheWriteTokens + countOr(model.cacheWriteTokens, 0),
      cacheReadTokens: acc.cacheReadTokens + countOr(model.cacheReadTokens, 0),
      totalTokens: acc.totalTokens + model.totalTokens,
      share: acc.share + model.share,
    }),
    {
      count: 0,
      uncachedInputTokens: 0,
      outputTokens: 0,
      cacheWriteTokens: 0,
      cacheReadTokens: 0,
      totalTokens: 0,
      share: 0,
    },
  );
}

/**
 * One line of the mix: a named model or the aggregate remainder. Both
 * the strip segment and the row are rendered from this, so a segment
 * and the row that explains it cannot come from different data.
 */
interface ModelMixEntry {
  readonly key: string;
  readonly share: number;
  readonly swatchClass: string;
  readonly segmentTestId: string | null;
  readonly name: string;
  readonly origin: string | null;
  readonly detail: string;
  readonly total: string;
  readonly rowTestId: string;
}

/**
 * The vendor line for a model row, or `null` when the field is not
 * there.
 *
 * `vendor` is required by the contract and every deployed server sends
 * it — but "the contract says required" is not "the running server is
 * sending it". A console can ship ahead of the service it reads (it did
 * this week), and a bare `model.vendor.length` on a payload without the
 * field is a TypeError thrown during render, which React answers by
 * unmounting the tree: an empty `<body>`, no error copy, no way back.
 * One absent string must not cost the whole dashboard.
 */
function vendorLine(vendor: string | undefined): string | null {
  return typeof vendor === 'string' && vendor.length > 0 ? vendor : null;
}

/**
 * Model share for the window: one proportional ramp plus the same
 * models as rows, so the ramp's segments and the rows read as one
 * unit — swatch index and ramp index are the same slot.
 *
 * The ramp names at most `SERIES_LIMIT` models; everything past that is
 * summed into a single `Other` row rather than dropped. The panel
 * claims a share OF THE WINDOW, so its segments have to add up to the
 * window: slicing the tail off left the strip short by exactly the
 * tail's share — an unexplained gap — while the summary cards above
 * kept counting it, putting two irreconcilable totals for one window on
 * one screen (#48). The subtitle also switches to the capped wording,
 * because "top five plus an aggregate" is a different claim from "every
 * model".
 *
 * Rows are test-identified by vendor AND model: two vendors shipping the
 * same model name is normal, and identifying on the name alone gave two
 * rows one identity — a duplicate `data-testid` and, for a reader, two
 * indistinguishable lines. React keys additionally carry the index,
 * because even vendor+model is not guaranteed unique by the contract;
 * the list is a static, unsorted, unfiltered render of one response, so
 * position is a sound tiebreaker here.
 *
 * Segment widths and printed percents both come out of ONE `mixShares`
 * pass over the whole strip, and the segments are `shrink-0` so the
 * declared width is the drawn width. See `modelMix.ts` for why the old
 * 2% visibility floor had to go: it was paid for out of every other
 * segment (#53).
 */
export function ModelMixPanel({
  models,
}: {
  readonly models: readonly AdminUsageModelBreakdown[];
}): JSX.Element {
  const t = useT();
  const top = models.slice(0, SERIES_LIMIT);
  const remainder = summarizeRemainder(models.slice(SERIES_LIMIT));
  const entries: readonly ModelMixEntry[] = [
    ...top.map((model, index) => ({
      key: `${index}:${model.vendor}:${model.model}`,
      share: model.share,
      swatchClass: stackedBarsSwatchClass(index),
      segmentTestId: null,
      name: model.model,
      origin: vendorLine(model.vendor),
      detail: t('usage.admin.model_mix.io', {
        input: formatCompactCount(countOr(model.uncachedInputTokens, model.inputTokens)),
        output: formatCompactCount(model.outputTokens),
        write: formatCompactCount(countOr(model.cacheWriteTokens, 0)),
        read: formatCompactCount(countOr(model.cacheReadTokens, 0)),
      }),
      total: formatCompactCount(model.totalTokens),
      rowTestId: `usage-model-row-${testIdSlug(`${model.vendor}-${model.model}`)}`,
    })),
    ...(remainder === null
      ? []
      : [
          {
            key: 'other',
            share: remainder.share,
            swatchClass: OTHER_SWATCH_CLASS,
            segmentTestId: 'usage-model-mix-other-segment',
            name: t('usage.admin.model_mix.other'),
            origin: t('usage.admin.model_mix.other_models', { count: remainder.count }),
            detail: t('usage.admin.model_mix.io', {
              input: formatCompactCount(remainder.uncachedInputTokens),
              output: formatCompactCount(remainder.outputTokens),
              write: formatCompactCount(remainder.cacheWriteTokens),
              read: formatCompactCount(remainder.cacheReadTokens),
            }),
            total: formatCompactCount(remainder.totalTokens),
            rowTestId: 'usage-model-mix-other',
          },
        ]),
  ];
  const segments = mixShares(entries, (entry) => entry.share);
  return (
    <article
      data-testid="usage-model-mix-card"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('usage.model_mix.title')}
        subtitle={
          remainder === null
            ? t('usage.admin.model_mix.subtitle')
            : t('usage.admin.model_mix.subtitle_capped', { count: SERIES_LIMIT })
        }
      />
      {top.length === 0 ? (
        <p className="text-body text-ink-low" data-testid="usage-model-mix-empty">
          {t('usage.admin.model_mix.empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div
            className="flex h-2 overflow-hidden rounded-sm bg-active"
            data-testid="usage-model-mix-bar"
          >
            {segments.map(({ item, width }) => (
              <div
                key={item.key}
                data-testid={item.segmentTestId ?? undefined}
                className={`shrink-0 ${item.swatchClass}`}
                style={{ width }}
              />
            ))}
          </div>
          {segments.map(({ item, percentText }) => (
            <ModelMixRow
              key={item.key}
              swatchClass={item.swatchClass}
              name={item.name}
              origin={item.origin}
              detail={item.detail}
              total={item.total}
              share={percentText}
              testId={item.rowTestId}
            />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * One line of the model mix — a named model or the `Other` aggregate.
 * Both wear the same shape so the aggregate reads as a peer of the
 * rows it stands for rather than as a footnote; only the swatch and
 * the `origin` line tell them apart.
 *
 * `origin` is the vendor for a model row (the same second line
 * `/requests` already shows) and the aggregated model count for
 * `Other`; it is omitted when the server sends no vendor rather than
 * rendering an empty line.
 */
function ModelMixRow(props: {
  readonly swatchClass: string;
  readonly name: string;
  readonly origin: string | null;
  readonly detail: string;
  readonly total: string;
  readonly share: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 border-b border-line pb-3 last:border-b-0"
      data-testid={props.testId}
    >
      <span className={`mt-1 h-2.5 w-2.5 rounded-sm ${props.swatchClass}`} aria-hidden="true" />
      <div className="min-w-0">
        <p className="truncate font-mono text-body font-medium text-ink">{props.name}</p>
        {props.origin === null ? null : (
          <p className="truncate font-mono text-mono-label text-ink-low">{props.origin}</p>
        )}
        <p className="font-mono text-caption text-ink-low">{props.detail}</p>
      </div>
      <div className="text-right">
        <p className="font-mono text-body font-medium text-ink">{props.total}</p>
        <p className="font-mono text-caption text-ink-low">{props.share}</p>
      </div>
    </div>
  );
}

/**
 * Org-unit consumption as meters scaled to the largest unit — a
 * relative comparison, not a share of total, so a long tail stays
 * visible. Units the server left null are shown as `Unassigned` rather
 * than dropped, since unattributed usage is the interesting case.
 *
 * The 8-row cap is a readability limit, not a ramp limit: `Meter` is a
 * status gauge with one neutral tone, so rows do not consume ramp
 * slots.
 */
export function DepartmentMixPanel({
  departments,
}: {
  readonly departments: readonly AdminUsageDepartmentBreakdown[];
}): JSX.Element {
  const t = useT();
  const top = departments.slice(0, 8);
  const max = Math.max(1, ...top.map((department) => department.totalTokens));
  return (
    <article
      data-testid="usage-department-mix-card"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('usage.admin.department_mix.title')}
        subtitle={t('usage.admin.department_mix.subtitle')}
      />
      {top.length === 0 ? (
        <p className="text-body text-ink-low">{t('usage.admin.department_mix.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {top.map((department) => (
            <Meter
              key={department.department ?? 'unassigned'}
              label={department.department ?? t('usage.admin.department_mix.unassigned')}
              value={department.totalTokens}
              max={max}
              valueText={formatCompactCount(department.totalTokens)}
              testId={`usage-department-row-${testIdSlug(department.department ?? 'unassigned')}`}
            />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * Quality-of-service readout derived from request billing metadata.
 * Every value is nullable at the contract level — the backend reports
 * `null` for "not measured in this window", which renders as `--` and
 * must never be collapsed to a zero that reads like a measurement.
 *
 * Rate thresholds (comment-pinned so reviewers can challenge them in
 * one place): cache-hit reads `ok` above 20% (prompt caching is doing
 * real work), neutral below; rate-limited reads `warn` as soon as it
 * is non-zero and `bad` above 5% (sustained throttling). The header
 * chip is derived from the same number as the meter — it is NOT a
 * fixed "all green" badge, or the panel would report health it never
 * measured.
 */
export function ServiceHealthPanel({
  dashboard,
}: {
  readonly dashboard: AdminUsageDashboardResponse;
}): JSX.Element {
  const t = useT();
  const health = dashboard.routingHealth;
  const cacheRate = health.cacheHitRate ?? 0;
  const limitedRate = health.rateLimitedRate ?? 0;
  const calm = limitedRate < 0.01;
  return (
    <article
      data-testid="usage-service-health-card"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('usage.admin.service.title')}
        subtitle={t('usage.admin.service.subtitle')}
        action={
          calm ? (
            <Chip tone="ok" testId="usage-service-health-chip">
              {t('usage.admin.service.all_green')}
            </Chip>
          ) : (
            <Chip tone="warn" testId="usage-service-health-chip">
              {t('usage.admin.service.rate_limited')}
            </Chip>
          )
        }
      />
      <div className="flex flex-col gap-3">
        <Meter
          label={t('usage.admin.service.cache_hit_rate')}
          value={cacheRate}
          max={1}
          tone={cacheRate > 0.2 ? 'ok' : 'neutral'}
          valueText={formatNullablePercent(health.cacheHitRate)}
          testId="usage-health-cache-hit"
        />
        <Meter
          label={t('usage.admin.service.rate_limited')}
          value={limitedRate}
          max={1}
          tone={limitedRate > 0.05 ? 'bad' : limitedRate > 0 ? 'warn' : 'neutral'}
          valueText={formatNullablePercent(health.rateLimitedRate)}
          testId="usage-health-rate-limited"
        />
        <dl className="m-0 grid grid-cols-1 gap-2 border-t border-line-soft pt-3">
          <MetricRow
            label={t('usage.admin.service.requests_evaluated')}
            value={formatTokens(health.routesEvaluated)}
          />
          <MetricRow
            label={t('usage.admin.service.avg_latency')}
            value={formatLatencyMs(health.averageLatencyMs)}
          />
          <MetricRow
            label={t('usage.admin.service.recovery_events')}
            value={health.failoverEvents === null ? '--' : formatTokens(health.failoverEvents)}
          />
        </dl>
      </div>
    </article>
  );
}

/** One label/value line of a definition-list readout. */
function MetricRow(props: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
      <dt className="text-body text-ink-mid">{props.label}</dt>
      <dd className="m-0 font-mono text-body font-medium text-ink">{props.value}</dd>
    </div>
  );
}

/**
 * Flat (bucket, group) points → one series per group over the GIVEN
 * bucket axis, capped at `SERIES_LIMIT` biggest groups by summed
 * metric. A bucket the points never mention contributes 0 to every
 * series rather than disappearing — which is why the axis is a
 * parameter and not something this function infers.
 */
export function pivotTrend(
  points: readonly AdminUsageTrendPoint[],
  metric: TokenMetric,
  buckets: readonly string[],
): readonly StackedBarsSeries[] {
  const totals = new Map<string, { label: string; total: number }>();
  for (const point of points) {
    const current = totals.get(point.groupKey) ?? { label: point.groupLabel, total: 0 };
    totals.set(point.groupKey, {
      label: current.label,
      total: current.total + trendValue(point, metric),
    });
  }
  const topGroups = [...totals.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, SERIES_LIMIT);
  return topGroups.map(([groupKey, meta]) => ({
    id: groupKey,
    label: meta.label,
    values: buckets.map((bucket) =>
      points
        .filter((point) => point.bucket === bucket && point.groupKey === groupKey)
        .reduce((sum, point) => sum + trendValue(point, metric), 0),
    ),
  }));
}

/**
 * One bucket's height for the selected metric.
 *
 * `total` is the server's cache-inclusive total. A server that predates
 * that field still sums raw input and output, which is what this chart
 * used to draw. `in` is uncached input, with the same fallback onto
 * `inputTokens`. Cache write and read are 0 when absent — they are not
 * slices of `inputTokens`.
 */
function trendValue(point: AdminUsageTrendPoint, metric: TokenMetric): number {
  if (metric === 'in') return countOr(point.uncachedInputTokens, point.inputTokens);
  if (metric === 'out') return point.outputTokens;
  if (metric === 'write') return countOr(point.cacheWriteTokens, 0);
  if (metric === 'read') return countOr(point.cacheReadTokens, 0);
  return countOr(point.totalTokens, point.inputTokens + point.outputTokens);
}
